import { Button } from '@ps/isp-web-component-library';
import type { TranslationTag } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { useMemo, useState } from 'react';

import type { SearchMatch } from 'widgets/sports-menu-widget/model/types';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';

import SearchResult from '../../SearchResult/SearchResult';

import { TXTWCL_Show_More } from './ResultGroup.messages.json';
import styles from './ResultGroup.scss';

interface Props {
  titleTranslationTag: TranslationTag;
  resultsGroup: SearchMatch[];
}

export const INITIAL_MAX_RESULTS_COUNT = 5 as const;

export const ResultGroup = ({ titleTranslationTag, resultsGroup }: Props) => {
  const { buttonPressed } = useTrackers();
  const { formatMessage } = useRegulatoryIntl();
  const [maxResultsCount, setMaxResultsCount] = useState<number>(INITIAL_MAX_RESULTS_COUNT);
  const displayedResults = useMemo(
    () => resultsGroup.slice(0, maxResultsCount),
    [resultsGroup, maxResultsCount]
  );

  if (resultsGroup.length === 0) {
    return null;
  }

  const displayShowMoreButton = displayedResults.length < resultsGroup.length;

  const handleShowMoreClick = () => {
    setMaxResultsCount(maxResultsCount + INITIAL_MAX_RESULTS_COUNT);
    buttonPressed({ buttonName: 'Show More' });
  };

  const titleTranslation = formatMessage(titleTranslationTag);

  return (
    <section className={styles.container} aria-label={titleTranslation}>
      <h4 className={styles.title}>{titleTranslation}</h4>

      <ul className={styles.resultsContainer}>
        {displayedResults.map((match, index) => (
          <SearchResult key={index} alias={match.alias} link={match.link} />
        ))}
      </ul>

      {displayShowMoreButton && (
        <div className={styles.showMore}>
          <Button variant="tertiary" size="small" onClick={handleShowMoreClick}>
            {formatMessage(TXTWCL_Show_More)}
          </Button>
        </div>
      )}
    </section>
  );
};
